import React, { useContext, type ReactNode } from 'react';
import Navbar from '../components/Navbar';
import { ThemeContext } from '../contexts/ThemeContext';
import { Box, Typography, Button, Container, styled } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';

import LightModeLogo from '../assets/images/LT_FK.jpg';
import DarkModeLogo from '../assets/images/LT_FH.jpg';

import TestimonialsSlider, { Testimonial } from '../components/TestimonialsSlider';
import SocialMediaSidebar from '../components/SocialMediaSidebar';
import Footer from '../components/Footer';
import Tilt3D from '../components/Tilt3D';
import TopicIcon from '../components/TopicIcons';
import { BRAND, brandPalette } from '../theme/brand';

import { motion, useReducedMotion, type Variants } from 'framer-motion';

// A Tanulás Tréning oldal specifikus véleményei
const tanulasTreningTestimonials: Testimonial[] = [
  { quote: 'A tanulás tréning sokat segített a gyerekemnek, sokkal jobban tud fókuszálni és kevesebbet szorong a suliban. Nagy köszönet!', author: 'Petra', title: 'Szülő' },
  { quote: 'Elképesztő, mennyire élvezi a fiam a tanulást most már! A játékos módszerekkel könnyedén elsajátítja az anyagot. Csak ajánlani tudom!', author: 'Zoltán', title: 'Szülő' },
  { quote: 'Pedagógusként látom, hogy ezek a technikák valós változást hoznak. A gyerekek motiváltabbak és sokkal hatékonyabban tanulnak.', author: 'Éva', title: 'Pedagógus' },
  { quote: 'Sok új trükköt tanultam, amivel sokkal gyorsabban meg tudok tanulni mindent. A dolgozatok is jobban mennek!', author: 'Dani', title: '9 éves diák' },
  { quote: 'Régebben utáltam olvasni, de most már élvezem! Ráadásul gyorsabban is olvasok, mint a barátaim.', author: 'Léna', title: '10 éves diák' },
  { quote: 'Szülőként is hasznos tippeket kaptam, hogyan támogathatom jobban a gyerekem tanulását. Az egész családra pozitív hatással volt.', author: 'Kata', title: 'Szülő és résztvevő' },
];

const kinekSzol: ReactNode[] = [
  <>Akik <b>nehezen koncentrálnak</b></>,
  <>Akik sokat tanulnak, de <b>eredmények nélkül</b></>,
  <>Akik <b>jobb jegyeket</b> szeretnének</>,
  <>Akik <b>szorongnak a dolgozatok</b> miatt</>,
];

const mitVisztekHaza: ReactNode[] = [
  <><b>Fejlettebb memória és koncentráció</b></>,
  <><b>Új tanulási stratégiák és technikák</b></>,
  <><b>Növekedett önbizalom</b> és motiváció</>,
  <>A <b>stressz csökkentése</b></>,
];

const revealVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: (delay: number = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut', delay } }),
};

const Reveal = ({ children, delay = 0 }: { children: ReactNode; delay?: number }) => (
  <motion.div
    variants={revealVariants}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    custom={delay}
  >
    {children}
  </motion.div>
);

// Csillag alakú gomb (a drop-shadow a külső burkolón van, mert a clip-path levágná az árnyékot)
const StarButton = styled(Button)({
  width: '240px',
  height: '240px',
  backgroundColor: BRAND.yellow,
  color: BRAND.purpleDark,
  clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
  borderRadius: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 900,
  fontSize: '0.95rem',
  lineHeight: 1.2,
  textTransform: 'uppercase',
  paddingTop: '15px',
  transition: 'all 0.3s ease-in-out',
  textAlign: 'center',
  '&:hover': {
    backgroundColor: BRAND.yellowStar,
    transform: 'scale(1.08) rotate(5deg)',
  },
  '&:active': { transform: 'scale(0.95)' },
});

const StarWrap = ({ children }: { children: ReactNode }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      animate={reduce ? undefined : { y: [0, -10, 0] }}
      transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
      style={{ filter: 'drop-shadow(0 18px 18px rgba(0,0,0,0.45))' }}
    >
      {children}
    </motion.div>
  );
};

// Lebegő díszcsillagok a háttérben
const FloatingStar = ({ top, left, size, delay, color }: { top: string; left: string; size: number; delay: number; color: string }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      aria-hidden
      animate={reduce ? undefined : { y: [0, -18, 0], rotate: [0, 14, -10, 0] }}
      transition={{ duration: 6 + delay, repeat: Infinity, ease: 'easeInOut', delay }}
      style={{ position: 'absolute', top, left, width: size, height: size, pointerEvents: 'none', opacity: 0.85 }}
    >
      <svg viewBox="0 0 48 48" width={size} height={size}>
        <polygon points="24,4 29.5,17 43.5,18.5 33,27.8 36.2,41.5 24,34.3 11.8,41.5 15,27.8 4.5,18.5 18.5,17" fill={color} />
      </svg>
    </motion.div>
  );
};

// Hullámos elválasztó a szekciók között (nem dobozos, hanem folyamatos átmenet)
const Wave = ({ fill, flip = false }: { fill: string; flip?: boolean }) => (
  <Box sx={{ lineHeight: 0, transform: flip ? 'scaleY(-1)' : 'none' }} aria-hidden>
    <svg viewBox="0 0 1440 90" preserveAspectRatio="none" style={{ width: '100%', height: 60, display: 'block' }}>
      <path d="M0,45 C240,100 480,-5 720,45 C960,95 1200,0 1440,45 L1440,90 L0,90 Z" fill={fill} />
    </svg>
  </Box>
);

const STICKER_COLORS = ['#FFD54F', '#FF8A80', '#81D4FA', '#A5D6A7'];

const StarBullet = ({ color }: { color: string }) => (
  <svg viewBox="0 0 48 48" width={34} height={34} style={{ flexShrink: 0 }} aria-hidden>
    <polygon
      points="24,4 29.5,17 43.5,18.5 33,27.8 36.2,41.5 24,34.3 11.8,41.5 15,27.8 4.5,18.5 18.5,17"
      fill={color}
      stroke="rgba(0,0,0,0.25)"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="20.5" cy="25" r="1.7" fill="#5D4037" />
    <circle cx="27.5" cy="25" r="1.7" fill="#5D4037" />
    <path d="M20 29.5 Q24 33.5 28 29.5" fill="none" stroke="#5D4037" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// "Matrica" csempék: enyhén elforgatva, vastag 3D árnyékkal
const StickerList = ({ items, accent, pal, darkMode }: { items: ReactNode[]; accent: string; pal: ReturnType<typeof brandPalette>; darkMode: boolean }) => (
  <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
    {items.map((content, i) => (
      <Reveal key={i} delay={i * 0.08}>
        <Tilt3D max={7}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2.5,
              py: 2,
              borderRadius: '22px',
              transform: `rotate(${i % 2 === 0 ? -1.2 : 1.2}deg)`,
              backgroundColor: pal.card,
              border: `2px solid ${accent}`,
              boxShadow: darkMode
                ? `0 6px 0 ${accent}55, 0 16px 26px rgba(0,0,0,0.4)`
                : `0 6px 0 ${accent}55, 0 16px 26px rgba(109,27,119,0.15)`,
              transition: 'transform 0.25s ease, box-shadow 0.25s ease',
              '&:hover': { transform: 'rotate(0deg) translateY(-3px)' },
            }}
          >
            <StarBullet color={STICKER_COLORS[i % STICKER_COLORS.length]} />
            <Typography sx={{ color: pal.text, fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.4, textAlign: 'left' }}>{content}</Typography>
          </Box>
        </Tilt3D>
      </Reveal>
    ))}
  </Box>
);

const TanulasTreningPage = () => {
  const { darkMode } = useContext(ThemeContext)!;
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const pal = brandPalette(darkMode);

  const currentLogo = darkMode ? LightModeLogo : DarkModeLogo;
  const facebookLink = 'https://www.facebook.com/csillagtanoda';
  const bandColor = darkMode ? 'rgba(18, 10, 57, 0.6)' : 'rgba(109, 27, 119, 0.08)';

  const gradientText = {
    background: darkMode
      ? `linear-gradient(90deg, ${BRAND.yellow}, #FFFFFF)`
      : `linear-gradient(90deg, ${BRAND.purple}, ${BRAND.purpleLight})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  } as const;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: 'transparent', overflowX: 'hidden' }}>
      <Navbar />
      <SocialMediaSidebar />

      {/* 1. HERO – nincs doboz, a tartalom szabadon lebeg a háttéren */}
      <Box sx={{ position: 'relative', textAlign: 'center', pt: { xs: 12, md: 14 }, pb: { xs: 6, md: 8 }, px: 2 }}>
        <FloatingStar top="14%" left="8%" size={34} delay={0} color="#FFD54F" />
        <FloatingStar top="24%" left="86%" size={26} delay={1.2} color="#81D4FA" />
        <FloatingStar top="62%" left="12%" size={22} delay={2} color="#FF8A80" />
        <FloatingStar top="70%" left="82%" size={38} delay={0.6} color="#A5D6A7" />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 900,
                lineHeight: 1.15,
                fontSize: { xs: '2rem', sm: '2.6rem', md: '3.4rem' },
                mb: 2.5,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))',
                ...gradientText,
              }}
            >
              Tanulási Tréning – Fedezd fel a benned rejlő zsenit!
            </Typography>
            <Typography sx={{ color: pal.text, fontSize: { xs: '1.05rem', md: '1.3rem' }, lineHeight: 1.6, maxWidth: 720, mx: 'auto' }}>
              Célunk, hogy a tanulás többé ne nyűg, hanem élmény legyen! Fejlesszük a memóriát, a koncentrációt és a problémamegoldó képességet játékos eszközökkel, az egyéni tempódhoz igazodva.
            </Typography>
          </motion.div>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, md: 6 } }}>
            <StarWrap>
              <StarButton onClick={() => window.open(facebookLink, '_blank')}>
                Jelentkezz<br />most!
              </StarButton>
            </StarWrap>
          </Box>
        </Container>
      </Box>

      {/* 2. A TRÉNINGRŐL – szöveg balra, lebegő 3D rakéta jobbra */}
      <Wave fill={bandColor} />
      <Box sx={{ backgroundColor: bandColor, py: { xs: 4, md: 8 }, px: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, alignItems: 'center', gap: { xs: 4, md: 8 } }}>
            <Box sx={{ flex: 1.3 }}>
              <Reveal>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{ fontWeight: 900, mb: 2.5, fontSize: { xs: '1.7rem', md: '2.3rem' }, textAlign: { xs: 'center', md: 'left' }, ...gradientText }}
                >
                  A Tanulási Tréningről
                </Typography>
                <Typography sx={{ color: pal.text, lineHeight: 1.75, mb: 2, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                  Modern világunkban a tanulás egyre nagyobb kihívás elé állítja a gyermekeket. Nem elegendő a lexikális tudás elsajátítása, szükség van arra is, hogy a gyerekek megtanulják, hogyan tanuljanak hatékonyan és örömmel. Tanulás tréningünk pontosan ebben nyújt segítséget.
                </Typography>
                <Typography sx={{ color: pal.text, lineHeight: 1.75, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                  Célunk, hogy a gyermekek felfedezzék saját tanulási stílusukat, fejlesszék a memóriájukat és koncentrációjukat, miközben <b>játékos feladatokon keresztül</b> erősödik az önbizalmuk és a problémamegoldó képességük. A tréning során a gyerekek olyan eszközöket kapnak a kezükbe, amelyeket az iskolában és a mindennapi életben is alkalmazhatnak, így a tanulás stresszmentes és sikeres élménnyé válik számukra.
                </Typography>
              </Reveal>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Tilt3D max={14} sx={{ width: { xs: 220, md: 300 }, height: { xs: 220, md: 300 } }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: darkMode
                      ? 'radial-gradient(circle at 30% 25%, #C92BB5, #6D1B77 55%, #120A39)'
                      : 'radial-gradient(circle at 30% 25%, #F6C6EE, #C92BB5 60%, #6D1B77)',
                    boxShadow: '0 30px 50px rgba(0,0,0,0.4), inset 0 -12px 24px rgba(0,0,0,0.3), inset 0 10px 20px rgba(255,255,255,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transformStyle: 'preserve-3d',
                  }}
                >
                  <motion.div
                    animate={reduce ? undefined : { y: [0, -14, 0], rotate: [-4, 4, -4] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ transform: 'translateZ(60px)', filter: 'drop-shadow(0 18px 14px rgba(0,0,0,0.45))' }}
                  >
                    <TopicIcon id="tanulas-trening" size={150} />
                  </motion.div>
                </Box>
              </Tilt3D>
            </Box>
          </Box>
        </Container>
      </Box>
      <Wave fill={bandColor} flip />

      {/* 3. KINEK SZÓL / MIT VISZTEK HAZA – matrica-csempék */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 6, md: 8 } }}>
          <Box>
            <Reveal>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 900, mb: 3, textAlign: 'center', fontSize: { xs: '1.5rem', md: '1.9rem' }, color: pal.heading }}>
                Kinek szól a tréning?
              </Typography>
            </Reveal>
            <StickerList items={kinekSzol} accent={BRAND.purpleLight} pal={pal} darkMode={darkMode} />
          </Box>
          <Box>
            <Reveal>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 900, mb: 3, textAlign: 'center', fontSize: { xs: '1.5rem', md: '1.9rem' }, color: pal.heading }}>
                Mit visztek haza?
              </Typography>
            </Reveal>
            <StickerList items={mitVisztekHaza} accent={darkMode ? BRAND.yellow : BRAND.purple} pal={pal} darkMode={darkMode} />
          </Box>
        </Box>
      </Container>

      {/* 4. VÉLEMÉNYEK – doboz nélkül, a háttéren */}
      <Wave fill={bandColor} />
      <Box sx={{ backgroundColor: bandColor, py: { xs: 5, md: 8 }, px: 2 }}>
        <Container maxWidth="md">
          <Reveal>
            <Typography
              variant="h4"
              component="h2"
              sx={{ fontWeight: 900, mb: 7, textAlign: 'center', fontSize: { xs: '1.6rem', md: '2.3rem' }, ...gradientText }}
            >
              Diákjaink és szüleik véleménye
            </Typography>
          </Reveal>
          <TestimonialsSlider testimonials={tanulasTreningTestimonials} autoScroll interval={5500} />
        </Container>
      </Box>
      <Wave fill={bandColor} flip />

      {/* 5. CTA */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, px: 2, pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 8 } }}>
        <Reveal>
          <Typography variant="h4" sx={{ fontWeight: 900, textAlign: 'center', fontSize: { xs: '1.6rem', md: '2.3rem' }, mb: 2, color: pal.heading }}>
            Kezdjétek el a változást még ma!
          </Typography>
        </Reveal>

        <StarWrap>
          <StarButton onClick={() => window.open(facebookLink, '_blank')}>
            <FacebookIcon sx={{ mb: 0.5 }} />
            <span>Jelent-<br />kezés</span>
          </StarButton>
        </StarWrap>

        <Box
          component={motion.img}
          whileHover={{ scale: 1.1, rotate: -2 }}
          whileTap={{ scale: 0.9 }}
          src={currentLogo}
          alt="Letölthető tartalmak logó"
          sx={{ width: 150, height: 'auto', cursor: 'pointer', }}
          onClick={() => navigate('/letolthetotartalmak')}
        />
      </Box>

      <Footer />
    </Box>
  );
};

export default TanulasTreningPage;